import { CommonModule, formatDate } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnInit, OnDestroy, ElementRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialog,
} from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import Integer from "@zxing/library/esm/core/util/Integer";
import {
  Adjustment,
  CalibrationBlockType,
  CalibrationBlockColour,
  CalibrationBlockDto,
  CalibrationBlockOptionDto,
  CalibrationInformation,
  ElectricalEquipmentResponse,
  EquipmentInformation,
  MachineResponse,
  ManufacturerSpec,
  MeasurementResponse,
  PressureMachineResponse,
  MachineRating,
  PressureTestResult,
  ResultInformation,
  CalibrationResponseDlmBaseResponse,
  TestMachine,
} from "app/api/models";
import { CalibrationResponse } from "app/api/models/calibration-response";
import {
  CalibrationService,
  ElectricalEquipmentService,
  MachineService,
  MeasurementService,
  PressureMachineService,
  RepairsAndCalibrationsService,
} from "app/api/services";
import { DLMAppConfirmService } from "../../../shared/services/app-confirm/app-confirm.service";
import { AppLoaderService } from "app/core/services/app-loader/app-loader.service";
import { UserRoleService } from "../../../shared/services/auth/user-role.service";
import { Form10aStep5DialogComponent } from "./form10a-step5-dialog/form10a-step5-dialog.component";
import { FlexLayoutModule } from "@angular/flex-layout";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DLMSharedModule } from "app/views/calibrations/shared/shared.module";
import { DragScrollModule } from "ngx-drag-scroll";
import { NgxPrintModule } from "ngx-print";
import { BehaviorSubject, Subject } from 'rxjs';
import { takeUntil, debounceTime, switchMap, distinctUntilChanged, filter } from 'rxjs/operators';
import { LocalStoreService } from "app/core/services/local-store.service";
import { FuseConfirmationService } from "@fuse/services/confirmation";
import { AppAuthService } from "app/api/services/cookie-jwt-auth/app-auth.service";
import { sortOnProperty } from "app/app.component";
import { MatTabsModule } from "@angular/material/tabs";

// Retained for the form10a step-2 read-only block display, which still maps the legacy
// CalibrationBlockType enum to a label (Phase 3 will cut that reader over to the DB option name).
export interface CalibrationBlockOption {
  value: CalibrationBlockType;
  viewValue: string;
}

export interface CalibrationBlockColourOption {
  value: CalibrationBlockColour;
  name: string;
  colour: string;
  secondaryColour?: string;
  textColour?: string; // for dark swatches (e.g. Black) where the default text is unreadable
}

@Component({
  selector: "app-calibration-form-dialog",
  templateUrl: "./calibration-form-dialog.component.html",
  styleUrls: ["./calibration-form-dialog.component.scss", "./../../../_styles/calibration-overrides.scss"],
  standalone: true,
  imports: [
    CommonModule,
    DLMSharedModule,
    MatListModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatInputModule,
    MatMenuModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    FlexLayoutModule,
    DragScrollModule,
    NgxPrintModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatRadioModule,
    MatTabsModule,
  ]
})
export class CalibrationFormDialogComponent implements OnInit, OnDestroy {
  @ViewChild('calibrationForm') formElement!: ElementRef<HTMLElement>;
  
  private saveSubject = new BehaviorSubject<{resultId: number | null, model: any} | null>(null);
  private destroy$ = new Subject<void>();
  private lastSavePromise: Promise<any> | null = null;

  model: CalibrationResponse;
  archivedRuns: ResultInformation[];
  archivedAddRuns: ResultInformation[];
  showArchivedRuns: boolean = false;
  showAddArchivedRuns: boolean = false;

  loaded: boolean = false;
  disableEdit: boolean = false;
  accessLevel: string;
  isPressureTest: boolean = false;

  adjustment = Adjustment;
  pressureTestResult = PressureTestResult;
  machineRating = MachineRating;

  // DB-driven block options (admin-managed via calibration/settings/calibration-blocks). Loaded in
  // ngOnInit. Replaces the old hardcoded CalibrationBlockType list — selecting an option now drives
  // both the saved block and its colour.
  blockOptions: CalibrationBlockOptionDto[] = [];

  blockColourList: CalibrationBlockColourOption[] = [
    { value: CalibrationBlockColour.Red, name: "Red", colour: "#a83232" },
    { value: CalibrationBlockColour.Blue, name: "Blue", colour: "#324ea8" },
    { value: CalibrationBlockColour.Yellow, name: "Yellow", colour: "#e1ff00" },
    { value: CalibrationBlockColour.Green, name: "Green", colour: "#32a852" },
    { value: CalibrationBlockColour.White, name: "White", colour: "#ffffff" },
    { value: CalibrationBlockColour.Silver, name: "Silver", colour: "#C0C0C0" },
    { value: CalibrationBlockColour.Gold, name: "Gold", colour: "#FFD700" },
    {
      value: CalibrationBlockColour.RedBlue,
      name: "Red/Blue",
      colour: "#a83232",
      secondaryColour: "#324ea8",
    },
    { value: CalibrationBlockColour.Black, name: "Black", colour: "#000000", textColour: "#ffffff" },
  ];

  elementResistanceList: string[] = [
    "120 Ohm",
    "350 Ohm",
    "700 Ohm",
    "1000 Ohm",
    "2000 Ohm",
    "5000 Ohm",
    "Mechanical",
    "Unknown",
    "N/A",
  ];

  sensingElementList: { name: string; value: number }[] = [
    { name: "Strain Bridge", value: 1 },
    { name: "Mechanical", value: 2 },
    { name: "Pressure Gauge", value: 3 },
    { name: "Pressure Sensor", value: 4 },
  ];

  calibrationFormGroup: FormGroup;

  machineList: MachineResponse[];
  pressureMachineList: PressureMachineResponse[];
  electricalEquipmentList: ElectricalEquipmentResponse[];
  measurementList: MeasurementResponse[];

  now: Date = new Date();

  Math = Math;
  ManufacturerSpec = ManufacturerSpec;

  private calibrationAlreadyComplete: boolean = false;

  constructor(
    private fb: FormBuilder,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<CalibrationFormDialogComponent>,
    public calibrationService: CalibrationService,
    private confirmService: DLMAppConfirmService,
    private loader: AppLoaderService,
    private snack: MatSnackBar,
    private machineService: MachineService,
    private pressureMachineService: PressureMachineService,
    private electricalEquipmentService: ElectricalEquipmentService,
    private measurementService: MeasurementService,
    public repairsService: RepairsAndCalibrationsService,
    private userRoleService: UserRoleService,
    private ls: LocalStoreService,
    private _fuseConfirmationService: FuseConfirmationService,
    public appAuth: AppAuthService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    if (!this.data) return;

    this.disableEdit = this.data.disableEdit;
    this.accessLevel = this.userRoleService.getCurrentUserRole();

    //todo perhaps load from offline if exists?

    this.machineService
      .apiMachineMachinesGet$Json({ archived: false })
      .subscribe((res) => {
        this.machineList = res.data;
      });

    this.pressureMachineService
      .apiPressureMachinePressureMachinesGet$Json({ archived: false })
      .subscribe((res) => {
        this.pressureMachineList = res.data;
      });

    this.electricalEquipmentService
      .apiElectricalEquipmentElectricalEquipmentsGet$Json({ archived: false })
      .subscribe((res) => {
        this.electricalEquipmentList = res.data;
      });

    this.measurementService
      .apiMeasurementMeasurementsGet$Json({ archived: false })
      .subscribe((res) => {
        this.measurementList = res.data;
      });

    this.calibrationService
      .apiCalibrationCalibrationBlocksGet$Json({ archived: false })
      .subscribe((res) => {
        this.blockOptions = res.data ?? [];
      });

    if (this.data?.calibrationId) {
      this.calibrationService
        .apiCalibrationCalibrationGet$Json({ calibrationId: this.data?.calibrationId })
        .subscribe((res) => {
          this.model = res.data;
          this.calibrationAlreadyComplete = this.model.calibration.isComplete;
          this.createFormControls();
          this.loaded = true;
        });

        this.loadArchivedRuns();
        
    } else {
      this.calibrationService
        .apiCalibrationCalibrationPost$Json({
          body: { loadcellId: this.data?.loadcellId },
        })
        .subscribe((res) => {
          this.model = res.data;
          this.calibrationAlreadyComplete = this.model.calibration.isComplete;
          this.createFormControls();
          this.loaded = true;

          var currentUser = this.appAuth.getUser();
          if(currentUser)
            this.repairsService.apiRepairsAndCalibrationsAddHistoryPost$Json$Response({body: {
              timeStamp: new Date().toUTCString(),
              loadcellId: this.model.loadcellId,
              description: `<strong>${currentUser.firstName} ${currentUser.lastName}</strong> created a calibration`,
            }});
        });
    }

    this.dialogRef.beforeClosed().subscribe((res) => {
      this.saveFormChanges();
    });

    // Setup the save subject handler
    this.saveSubject.pipe(
      takeUntil(this.destroy$),
      filter(value => value !== null),
      debounceTime(300),
      distinctUntilChanged((prev, curr) => {
        if (!prev || !curr) return false;
        return JSON.stringify(prev.model) === JSON.stringify(curr.model);
      }),
      switchMap(value => {
        if (!value || !value.model) return [];
        return this.calibrationService.apiCalibrationEditCalibrationPost$Json({ 
          body: value.model 
        });
      })
    ).subscribe({
      next: (res: CalibrationResponseDlmBaseResponse) => {
        if (!res?.data) {
          console.error('Error saving form changes');
          return;
        }
        
        const currentValue = this.saveSubject.getValue();
        if (!currentValue) return;

        const { resultId } = currentValue;
        
        if (!resultId) {
          this.model = res.data;
        } else {
          // Find the list containing the updated result, then update result in the correct list
          if (
            this.model.calibration.resultList
              ?.map((x) => x.resultId)
              ?.includes(resultId)
          ) {
            const targetResult = this.model.calibration.resultList.find(
              (x) => x.resultId === resultId
            );
            const sourceResult = res.data.calibration.resultList?.find(
              (x) => x.resultId === resultId
            );
            if (targetResult && sourceResult) {
              targetResult.error = sourceResult.error;
            }
          } else if (
            this.model.calibration.additionalResultList
              ?.map((x) => x.resultId)
              ?.includes(resultId)
          ) {
            const targetResult = this.model.calibration.additionalResultList.find(
              (x) => x.resultId === resultId
            );
            const sourceResult = res.data.calibration.additionalResultList?.find(
              (x) => x.resultId === resultId
            );
            if (targetResult && sourceResult) {
              targetResult.error = sourceResult.error;
            }
          }
        }

        this.cdr.detectChanges();

        //everything went fine - remove the item from localstorage
        this.ls.clearCalibrationsForId(res.data.calibration.calibrationId);
        this.updateInspectedBy();
      },
      error: (error) => {
        console.error('Error saving form changes:', error);

        //status 0 = internet outage, status 500 = server error i.e. can't get to DB
        if(error?.status == 0 && error?.message?.includes('Http failure response')) {
          console.log("internet outage detected");
          this.doOfflineProcess();
        }
        else {
          this.snack.open('Error saving changes. Please try again.', 'OK', {
            duration: 3000
          });
        }
      }
    });
  }

  loadArchivedRuns() {
    if(!(!this.disableEdit || this.appAuth.isSuperAdmin()))
      return;

    this.calibrationService.apiCalibrationGetArchivedResultsGet$Json$Response({ calibrationId: this.data?.calibrationId }).subscribe(res => {
      if(!res?.body?.data) return;

      this.archivedRuns = res.body.data.filter(x => !x.isForAdditionalResult);
      this.archivedAddRuns = res.body.data.filter(x => x.isForAdditionalResult);
    });
  }

  doOfflineProcess(successCallback?: () => void) {
    const confirmation = this._fuseConfirmationService.open({
      title  : 'Offline Mode',
      message: `Calibration saved locally. Please remain on this screen and tap "retry" once you're back online to sync the calibration`,
      actions: {
          confirm: {
              label: 'Retry',
          },
          cancel: {
            show: false,
          }
      },
    });

    confirmation.afterClosed().subscribe(_ =>
    {
        this.loader.open();
        this.calibrationService.apiCalibrationPingGet$Json$Response().subscribe({
            next: (res) => {
              console.log("ping succeeded", res);
              console.log('Internet connection online again. Retrying Calibration save');

              const modelCopy = JSON.parse(JSON.stringify(this.model));
              this.calibrationService.apiCalibrationEditCalibrationPost$Json({body: modelCopy}).subscribe({
                  next: (res: CalibrationResponseDlmBaseResponse) => {
                    this.loader.close();
                    if (!res?.data) return;

                    console.log('Calibration successfully synced to Nexus');

                    this.ls.clearCalibrationsForId(modelCopy.calibration.calibrationId);
                    this.updateInspectedBy();

                    const successDialog = this._fuseConfirmationService.open({
                      title  : 'Calibration Saved - Internet Connected',
                      message: `Job #${modelCopy.loadcellId} has been saved and uploaded successfully. Internet connection is active, no further action needed`,
                      icon: {
                          color: 'success',
                      },
                      actions: {
                          confirm: {
                              label: 'OK',
                          },
                          cancel: {
                            show: false,
                          }
                      },
                    });

                    successDialog.afterClosed().subscribe(_ => {
                      if(successCallback)
                        successCallback();
                    });
                  },
                  error: (error) => {
                    this.loader.close();
                    if(error?.status == 0 && error?.message?.includes('Http failure response')) {
                      console.log("internet outage detected");
                      this.doOfflineProcess();
                    }
                    else {
                      this.snack.open('Error saving changes. Please try again.', 'OK', {
                        duration: 3000
                      });
                    }
                  }
              });
            },
            error: (res) => {
              this.loader.close();
              console.log("ping failed");
              console.log('Couldn\'t reach server - calibration remaining stored offline');
              this.doOfflineProcess(successCallback);
            }
        });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.saveSubject.complete();
  }

  updatePressureRadio(event: any) {
    this.model.calibration.pressureTestResult = Integer.parseInt(
      event.target.value
    );
  }

  updateLinkUnit(event: any) {
    this.model.calibration.linkUnit = event.target.value;
  }

  updateLinkUnitFirmware(event: any) {
    this.model.calibration.linkUnitFirmware = event.target.value;
  }

  updateHandheld(event: any) {
    this.model.calibration.handheld = event.target.value;
  }

  updateHandheldFirmware(event: any) {
    this.model.calibration.handheldFirmware = event.target.value;
  }

  updateRadioFirmware(event: any) {
    this.model.calibration.radioFirmware = event.target.value;
  }

  updateSWL(event: any) {
    this.model.calibration.swl = event.target.value;
  }

  updateProofLoad(event: any) {
    var value = event.target.value;

    if (isNaN(value)) value = "0";

    this.model.calibration.proofLoad = parseFloat(value.replace("%", "")) / 100;

    this.calibrationFormGroup.controls["proofLoad"].setValue(
      this.getProofLoad()
    );

    this.cdr.detectChanges();
  }

  getProofLoad() {
    return this.model.calibration.proofLoad * 100 + "%";
  }

  updatePressureRating(event: any) {
    this.model.calibration.pressureRating = event.target.value;
  }

  updateRange(event: any) {
    this.model.calibration.range = event.target.value;
  }

  updateOutput(event: any) {
    this.model.calibration.output = event.target.value;
  }

  updateResistance(event: any) {
    this.model.calibration.resistance = event.target.value;
  }

  addBlockLine() {
    if(this.model.calibration.blocks == null)
      this.model.calibration.blocks = [];

    // Default to the first DB option so a new row is valid. Colour is unlinked from the option
    // and picked manually, so a new row starts with none selected.
    const first = this.blockOptions[0];
    this.model.calibration.blocks.push({
      calibrationBlockOptionId: first?.id ?? null,
      blockNumber: '',
    });
  }

  // Store the option FK only — colour is unlinked from block names and set manually via the
  // Block Colour select, so choosing an option must not touch it.
  selectBlockOption(calibrationBlock: CalibrationBlockDto, optionId: number) {
    calibrationBlock.calibrationBlockOptionId = optionId;
  }

  selectBlockColour(calibrationBlock: CalibrationBlockDto, colour: CalibrationBlockColour) {
    calibrationBlock.blockColour = colour;
  }

  removeBlockLine(index: number) {
    this.model.calibration.blocks = this.model.calibration.blocks.filter((_, i) => i != index);
  }

  updateIsCompression(index: number) {
    switch(index) {
      case 0:
        this.model.calibration.isCompression = false;
        break;
      case 1:
        this.model.calibration.isCompression = true;
        break;
      case 2:
        this.model.calibration.isCompression = null;
        break;
    }
  }

  updateAdditionalIsCompression(index: number) {
    switch(index) {
      case 0:
        this.model.calibration.additionalIsCompression = false;
        break;
      case 1:
        this.model.calibration.additionalIsCompression = true;
        break;
      case 2:
        this.model.calibration.additionalIsCompression = null;
        break;
    }
  }

  getBackgroundColourString(blockColour: CalibrationBlockColour) {
    if (!blockColour) return;

    var entry = this.blockColourList.find((x) => x.value == blockColour);
    if (!entry) return;

    if (entry.secondaryColour)
      return this.stripedCss(entry.colour, entry.secondaryColour);

    var css = "background-color: " + entry.colour + ";";
    if (entry.textColour) css += " color: " + entry.textColour + ";";
    return css;
  }

  stripedCss(colour1: string, colour2: string) {
    return (
      "background: repeating-linear-gradient(45deg, " +
      colour1 +
      ", " +
      colour1 +
      " 10px," +
      colour2 +
      " 10px," +
      colour2 +
      " 20px);"
    );
  }

  updateTestMachine(event: any, isAdditional: boolean = false) {
    if (!isAdditional)
      this.model.calibration.machineRating = Integer.parseInt(
        event.target.value
      );
    else
      this.model.calibration.additionalMachineRating = Integer.parseInt(
        event.target.value
      );

    this.saveFormChanges();
  }

  updateMultimeter(event: any) {
    this.model.calibration.multimeter = event.target.value;
  }

  updateMachine(
    equipment: EquipmentInformation,
    $event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.equipmentList.find(
        (x) => x.equipmentId == equipment.equipmentId
      ).machine = $event.value;
    } else {
      this.model.calibration.additionalEquipmentList.find(
        (x) => x.equipmentId == equipment.equipmentId
      ).machine = $event.value;
    }

    this.cdr.detectChanges();
  }

  getMachine(equipment: any) {
    return this.machineList?.find(
      (x) => x.machineId == equipment?.machine?.machineId
    );
  }

  getPressureMachine(equipment: any) {
    return this.pressureMachineList?.find(
      (x) => x.pressureMachineId == equipment?.pressureMachineId
    );
  }

  getElectricalEquipment(equipment: any) {
    return this.electricalEquipmentList?.find(
      (x) => x.electricalEquipmentId == equipment?.electricalEquipmentId
    );
  }

  updateMeasuredIn(event: any, isAdditional: boolean = false) {
    if (!isAdditional) this.model.calibration.measurementResponse = event.value;
    else this.model.calibration.additionalMeasurementResponse = event.value;
  }

  updateTestMachineUnit(event: any, isAdditional: boolean = false) {
    if (!isAdditional)
      this.model.calibration.testMachineMeasurementResponse = event.value;
    else
      this.model.calibration.additionalTestMachineMeasurementResponse =
        event.value;
  }

  getMeasurement(measurementId: number) {
    return this.measurementList?.find((x) => x.measurementId == measurementId);
  }

  private _resolution: string;
  private _additionalResolution: string;

  get resolution(): string {
    return this._resolution;
  }

  get additionalResolution(): string {
    return this._additionalResolution;
  }

  set resolution(value: string) {
    this._resolution = value
      .toString()
      .replace(/(\.\d*?[1-9])0+$/, "$1")
      .replace(/\.$/, "");
    this.model.calibration.resolution = parseFloat(this._resolution);
  }

  set additionalResolution(value: string) {
    this._additionalResolution = value
      .toString()
      .replace(/(\.\d*?[1-9])0+$/, "$1")
      .replace(/\.$/, "");
    this.model.calibration.additionalResolution = parseFloat(
      this._additionalResolution
    );
  }

  getResolutionScale(isAdditional: boolean = false) {
    var value = isAdditional
      ? this.model.calibration?.additionalResolution ?? 0
      : this.model.calibration?.resolution ?? 0;

    // Convert to a string
    const valueAsString = value.toString();

    // Check if there is a decimal point in the string
    if (valueAsString.includes(".")) {
      // The number of decimal places is the length of the substring after the decimal point
      var scale = valueAsString.split(".")[1].length;
      return scale;
    } else {
      return 0;
    }
  }

  getRoundingFactor(scale: number) {
    var factor = Math.pow(10, scale);
    return factor;
  }

  getScaledValue(value: number, isAdditional: boolean = false) {
    var scale = this.getResolutionScale(isAdditional);
    var factor = this.getRoundingFactor(scale);
    var result = (Math.round(value * factor) / factor).toFixed(
      scale > 0 ? scale : 0
    );

    return result;
  }

  updateMachineReadings(
    result: ResultInformation,
    event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.resultList.find(
        (x) => x.resultId == result.resultId
      ).machineReadings = parseFloat(event.target.value);
    } else {
      this.model.calibration.additionalResultList.find(
        (x) => x.resultId == result.resultId
      ).machineReadings = parseFloat(event.target.value);
    }

    this.saveFormChanges(result.resultId);
  }

  updateDesired(
    result: ResultInformation,
    event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.resultList.find(
        (x) => x.resultId == result.resultId
      ).desired = parseFloat(event.target.value);
    } else {
      this.model.calibration.additionalResultList.find(
        (x) => x.resultId == result.resultId
      ).desired = parseFloat(event.target.value);
    }

    this.saveFormChanges(result.resultId);
  }

  isRequired(result: CalibrationInformation, isAdditional: boolean = false) {
    var measuredIn = !isAdditional
      ? result.measurementResponse?.name?.toLowerCase()
      : result.additionalMeasurementResponse?.name?.toLowerCase();
    return measuredIn == "milliamps" || measuredIn == "ma";
  }

  updateRun1(
    result: ResultInformation,
    event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.resultList.find(
        (x) => x.resultId == result.resultId
      ).run1 = parseFloat(event.target.value);
    } else {
      this.model.calibration.additionalResultList.find(
        (x) => x.resultId == result.resultId
      ).run1 = parseFloat(event.target.value);
    }

    this.saveFormChanges(result.resultId);
  }

  updateRun2(
    result: ResultInformation,
    event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.resultList.find(
        (x) => x.resultId == result.resultId
      ).run2 = parseFloat(event.target.value);
    } else {
      this.model.calibration.additionalResultList.find(
        (x) => x.resultId == result.resultId
      ).run2 = parseFloat(event.target.value);
    }

    this.saveFormChanges(result.resultId);
  }

  updateRun3(
    result: ResultInformation,
    event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.resultList.find(
        (x) => x.resultId == result.resultId
      ).run3 = parseFloat(event.target.value);
    } else {
      this.model.calibration.additionalResultList.find(
        (x) => x.resultId == result.resultId
      ).run3 = parseFloat(event.target.value);
    }

    this.saveFormChanges(result.resultId);
  }

  updateRun4(
    result: ResultInformation,
    event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.resultList.find(
        (x) => x.resultId == result.resultId
      ).run4 = parseFloat(event.target.value);
    } else {
      this.model.calibration.additionalResultList.find(
        (x) => x.resultId == result.resultId
      ).run4 = parseFloat(event.target.value);
    }

    this.saveFormChanges(result.resultId);
  }

  updateRun5(
    result: ResultInformation,
    event: any,
    isAdditional: boolean = false
  ) {
    if (!isAdditional) {
      this.model.calibration.resultList.find(
        (x) => x.resultId == result.resultId
      ).run5 = parseFloat(event.target.value);
    } else {
      this.model.calibration.additionalResultList.find(
        (x) => x.resultId == result.resultId
      ).run5 = parseFloat(event.target.value);
    }

    this.saveFormChanges(result.resultId);
  }

  toggleIncludeRun(run: number, $event: any, isAdditional: boolean = false) {
    if (!isAdditional) {
      switch (run) {
        case 1:
          this.model.calibration.includeRun1 = $event.checked;
          break;
        case 2:
          this.model.calibration.includeRun2 = $event.checked;
          break;
        case 3:
          this.model.calibration.includeRun3 = $event.checked;
          break;
        case 4:
          this.model.calibration.includeRun4 = $event.checked;
          break;
        case 5:
          this.model.calibration.includeRun5 = $event.checked;
          break;
      }
    } else {
      switch (run) {
        case 1:
          this.model.calibration.additionalIncludeRun1 = $event.checked;
          break;
        case 2:
          this.model.calibration.additionalIncludeRun2 = $event.checked;
          break;
        case 3:
          this.model.calibration.additionalIncludeRun3 = $event.checked;
          break;
        case 4:
          this.model.calibration.additionalIncludeRun4 = $event.checked;
          break;
        case 5:
          this.model.calibration.additionalIncludeRun5 = $event.checked;
          break;
      }
    }
    this.saveFormChanges();
  }

  toggleIncludeError($event: any) {
    this.model.calibration.includeError = $event.checked;
    this.saveFormChanges();
  }

  updateAdjustmentRequiredRadio(event: any) {
    this.model.calibration.adjustment = Integer.parseInt(event.target.value);
  }

  updateManuSpecRadio(event: any) {
    this.model.calibration.manuSpec = JSON.parse(event.target.value);
  }

  updateSignature(event: any) {
    this.model.calibration.signature = event;
  }

  private dateTimeout: NodeJS.Timeout;

  updateDate(event: any) {
    clearTimeout(this.dateTimeout);
    this.dateTimeout = setTimeout(() => {
      this.model.calibration.date = new Date(event.target.value).toISOString();
    }, 500);
  }

  getDate() {
    return this.model.calibration.date.split("T")[0];
  }

  saveFormChanges(resultId: number = null) {
    // Create a deep copy of the model to prevent reference issues
    const modelCopy = JSON.parse(JSON.stringify(this.model));
    this.ls.storeCalibration(modelCopy);
    this.saveSubject.next({resultId, model: modelCopy});
  }

  addEquipmentLine(isAdditional: boolean = false) {
    if (!isAdditional) this.model.calibration.equipmentList.push({});
    else
      this.model.calibration.additionalEquipmentList.push({
        isForAdditionalResult: true,
      });
    this.saveFormChanges();
  }

  addResultsLine(isAdditional: boolean = false) {
    console.log("add results line, additional?", isAdditional);
    if (!isAdditional) {
      this.model.calibration.resultList.push({
        isForAdditionalResult: false,
      });
    } else {
      if (!this.model.calibration.additionalResultList)
        this.model.calibration.additionalResultList = [];

      this.model.calibration.additionalResultList.push({
        isForAdditionalResult: true,
      });
    }
    console.log("resultList", this.model.calibration.resultList);
    console.log("additionalList", this.model.calibration.additionalResultList);
    this.saveFormChanges();
  }

  reinstateResultLine(result: ResultInformation) {
    this.calibrationService.apiCalibrationReinstateResultGet$Json$Response({resultId: result.resultId}).subscribe(res => {
      if(!res?.body?.data) return;

      //insert it
      if(!result.isForAdditionalResult) {
        this.model.calibration.resultList.push(result);
        this.model.calibration.resultList = sortOnProperty(this.model.calibration.resultList, 'machineReadings');
        this.archivedRuns = this.archivedRuns.filter(x => x.resultId != result.resultId);
      }
      else {
        if (!this.model.calibration.additionalResultList)
          this.model.calibration.additionalResultList = [];

        this.model.calibration.additionalResultList.push(result);
        this.model.calibration.additionalResultList = sortOnProperty(this.model.calibration.additionalResultList, 'machineReadings');
        this.archivedAddRuns = this.archivedAddRuns.filter(x => x.resultId != result.resultId);
      }

      this.cdr.detectChanges();
      this.saveFormChanges();
    });
  }

  deleteEquipmentLine(equipment: EquipmentInformation) {
    this.calibrationService
      .apiCalibrationDeleteEquipmentPost$Json({
        body: this.model,
        equipmentId: equipment.equipmentId,
      })
      .subscribe((res) => {
        this.model = res?.data;
        this.cdr.detectChanges();
      });
  }

  deleteResultLine(result: ResultInformation) {
    this.calibrationService
      .apiCalibrationDeleteResultPost$Json({ body: this.model, resultId: result.resultId })
      .subscribe((res) => {
        this.model = res?.data;
        this.cdr.detectChanges();
        console.log("resultList", this.model.calibration.resultList);
        console.log("additionalList", this.model.calibration.additionalResultList);

        this.loadArchivedRuns();
      });
  }

  checkFormValidation() {
    this.calibrationFormGroup.markAllAsTouched();
    this.calibrationFormGroup.markAsDirty();
    
    var isValid =
      this.calibrationFormGroup.valid &&
      this.model.calibration.resultList.length != 0 &&

      this.model.calibration.resultList.filter(x => x.machineReadings != null).length == this.model.calibration.resultList.length &&
      this.model.calibration.additionalResultList?.filter(x => x.machineReadings != null).length == this.model.calibration.additionalResultList?.length &&

      this.model?.calibration?.electricalEquipment &&
      (!this.model?.calibration?.additionalResultsSet || this.model?.calibration?.additionalElectricalEquipment) &&

      this.model?.calibration?.blocks?.length &&

      this.model?.calibration?.measurementResponse?.measurementId &&
      this.model?.calibration?.resistance != null &&
      (!this.additionalResolution ||
        this.model.calibration.additionalResultList.length != 0) &&
      (!this.additionalResolution ||
        this.model?.calibration?.additionalMeasurementResponse?.measurementId);

    if (!isValid) {
      this.snack.open("Please fill out all required fields", "OK", {
        duration: 3000,
      });

      return false;
    }

    return isValid;
  }

  async checkResultTolerances() {
    //check if any results are more than 5% out from expected
    var runs = this.model.calibration.resultList?.filter(x => x.desired != 0).map((x, i) => ({line: i+1, desired: x.desired, runs: [x.run1, x.run2, x.run3, x.run4, x.run5]})) ?? [];
    var runsA = this.model.calibration.additionalResultList?.filter(x => x.desired != 0).map((x, i) => ({line: i+1, desired: x.desired, runs: [x.run1, x.run2, x.run3, x.run4, x.run5]})) ?? [];

    function isOverTolerance (desired: number, run: number) {
      return run != null && desired != null && (Math.abs(desired - run) / Math.abs(desired) >= 0.05);
    }

    var overTolerance = runs.filter(x => x.runs.some(run => isOverTolerance(x.desired, run)));
    var overToleranceA = runsA.filter(x => x.runs.some(run => isOverTolerance(x.desired, run)));

    if(overTolerance.length == 0 && overToleranceA.length == 0)
      return true;

    var overText = '';

    if(overTolerance.length) {
      overText += '<span class="-ml-12">Result rows:</span>';
      overText += overTolerance.map(x =>
        `
          <div class='grid grid-cols-6 gap-2 -ml-12 -mr-10'>
            <span>Line ${x.line}</span>
            ${x.runs.map(run => `<span ${isOverTolerance(x.desired, run) ? 'class="text-warn font-bold"' : ''}>${this.getScaledValue(run, false)}</span>`).join('')}
          </div>
        `
      ).join('');
    }
    if(overToleranceA.length) {
      if(overTolerance.length)
        overText += '<br/><br/>';

      overText += '<span class="-ml-12">Additional results:</span>'
      overText += overToleranceA.map(x =>
        `
          <div class='grid grid-cols-6 gap-2 -ml-12 -mr-10'>
            <span>Line ${x.line}</span>
            ${x.runs.map(run => `<span ${isOverTolerance(x.desired, run) ? 'class="text-warn font-bold"' : ''}>${this.getScaledValue(run, true)}</span>`).join('')}
          </div>
        `
      ).join('');
    }

    const dialog = this._fuseConfirmationService.open({
      title  : 'Double Check Calibration Values',
      message: `One or more results deviate by more than ±5% of the Desired value.<br/><br/>
                Please review the highlighted values and only proceed if you are certain they are correct.<br/><br/>${overText}`,
      icon: {
          color: 'success',
      },
      actions: {
          confirm: {
              label: 'Proceed Anyway',
          },
          cancel: {
            label: 'Review Values',
          }
      },
      justifyButtonsBetween: true,
    });

    var result = await dialog.afterClosed().toPromise();

    return result === 'confirmed';
  }

  async saveHandler() {
    if(!this.checkFormValidation())
      return;

    var tolerances = await this.checkResultTolerances();
    if(!tolerances)
      return;
    
    this.confirmService
      .confirm({
        title: `Confirm Submission`,
        message: `Are you sure you would like to finish and submit this calibration?`,
        confirmText: `Finish`,
        isDelete: false,
      })
      .subscribe((res) => {
        if (res) {
          this.loader.open();
          this.model.calibration.isComplete = true;
          this.ls.storeCalibration(this.model);
          
          this.calibrationService
            .apiCalibrationEditCalibrationPost$Json({ body: this.model })
            .subscribe({
              next: (res) => {
                this.loader.close();

                if(!res?.data) {
                  console.error('Error saving form changes');
                  return;
                }

                this.model = res?.data;
                this.model['wasComplete'] = this.calibrationAlreadyComplete;

                //everything went fine - remove the item from localstorage
                this.ls.clearCalibrationsForId(res.data?.calibration.calibrationId);
                this.updateInspectedBy();

                //fill out form10a step 5 first
                var dialog = this.dialog.open(Form10aStep5DialogComponent, {
                  panelClass: "form10a-step5-dialog",
                  data: { loadcellId: this.model.loadcellId },
                });
                dialog.afterClosed().subscribe((res) => {
                  //always ask if need to go back and pressure test
                  this.showPressureTestDialog();
                });
              },

              error: (error) => {
                this.loader.close();
                console.error('Error saving form changes:', error);

                //status 0 = internet outage, status 500 = server error i.e. can't get to DB
                if(error?.status == 0 && error?.message?.includes('Http failure response')) {
                  console.log("internet outage detected");
                  this.doOfflineProcess(() => {
                    var dialog = this.dialog.open(Form10aStep5DialogComponent, {
                      panelClass: "form10a-step5-dialog",
                      data: { loadcellId: this.model.loadcellId },
                    });
                    dialog.afterClosed().subscribe((res) => {
                      //always ask if need to go back and pressure test
                      this.showPressureTestDialog();
                    });
                  });
                }
                else {
                  this.snack.open('Error saving changes. Please try again.', 'OK', {
                    duration: 3000
                  });
                }
              }

            }
          );
        }
      });
    
  }

  showPressureTestDialog() {
    const confirmation = this._fuseConfirmationService.open({
      title  : 'Pressure Test',
      message: `Do you need to go back and fill out the pressure test?`,
      actions: {
          confirm: {
              label: 'Yes',
              color: 'primary',
          },
          cancel: {
            label: 'No',
          },
          additionalButton: {
            label: 'Park for Later Date',
            // callback: () => {
            //   console.log("parking for later date");
            //   var currentUser = this.appAuth.getUser();

            //   this.repairsService.apiRepairsAndCalibrationsUpdateTestMachinePost$Json$Response({
            //     loadcellId: this.model.loadcellId,
            //     testMachine: TestMachine.PressureTest,
            //     userName: `${currentUser.firstName} ${currentUser.lastName}`,
            //   });
            // },
          },
        },
    });

    confirmation.afterClosed().subscribe((result) => {
      if (result === 'confirmed') {
        this.isPressureTest = true;
        this.findScrollableAncestor(this.formElement.nativeElement)?.scrollTo({ top: 0, behavior: 'smooth' });
      }
      else if (result === 'additional') {
        this.model['setPressureTestMachine'] = true;
        this.close(this.model);
      }
      else
        this.close(this.model);
    });
  }

  updateInspectedBy() {
    var user = this.appAuth.getUser();
    var currentUserId = user?.id;
    if(currentUserId)
        this.repairsService.apiRepairsAndCalibrationsUpdateInspectedByPost$Json({
            employeeId: currentUserId,
            loadcellId: this.model.loadcellId,
            userName: `${user.firstName} ${user.lastName}`,
        }).subscribe();
  }

  close(result: CalibrationResponse) {
    result.calibration['wasComplete'] = this.calibrationAlreadyComplete;
    result.calibration['fromPressureTest'] = this.isPressureTest;
    this.dialogRef.close(result);
  }

  createFormControls() {
    this.calibrationFormGroup = this.fb.group({
      bookingRef: [this.model.calibration.bookingRef, Validators.required],
      tW30LinkUnitSN: [this.model.calibration.linkUnit, Validators.required],
      linkUnitFirmware: [this.model.calibration.linkUnitFirmware],
      bar: [this.model.calibration.linkUnitFirmware],
      tW30HDDSN: [this.model.calibration.handheld, Validators.required],
      radioFirmware: [
        this.model.calibration.radioFirmware,
        Validators.required,
      ],
      handHeldFirmware: [this.model.calibration.handheldFirmware],
      sWL: [this.model.calibration.swl, Validators.required],
      proofLoad: [this.getProofLoad(), Validators.required],
      pressureRating: [this.model.calibration.pressureRating],
      range: [this.model.calibration.range],
      mVVOutputAtSWL: [this.model.calibration.output, Validators.required],
      sensingElement: [
        this.model.calibration.sensingElement,
        Validators.required,
      ],
      elementResistance: [this.model.calibration.resistance],
      multiMeterSN: [this.model.calibration.multimeter],
      resolution: [this.model.calibration.resolution],
      run1: [this.model.calibration.includeRun1, Validators.required],
      run2: [this.model.calibration.includeRun2, Validators.required],
      run3: [this.model.calibration.includeRun3, Validators.required],
      run4: [this.model.calibration.includeRun4, Validators.required],
      run5: [this.model.calibration.includeRun5, Validators.required],
      date: [
        formatDate(this.model.calibration.date, "yyyy-MM-dd", "en"),
        Validators.required,
      ],

      additionalResolution: [this.model.calibration.additionalResolution],
      additionalRun1: [
        this.model.calibration.additionalIncludeRun1,
        Validators.required,
      ],
      additionalRun2: [
        this.model.calibration.additionalIncludeRun2,
        Validators.required,
      ],
      additionalRun3: [
        this.model.calibration.additionalIncludeRun3,
        Validators.required,
      ],
      additionalRun4: [
        this.model.calibration.additionalIncludeRun4,
        Validators.required,
      ],
      additionalRun5: [
        this.model.calibration.additionalIncludeRun5,
        Validators.required,
      ],
    });
  }
  
  private findScrollableAncestor(start: HTMLElement): HTMLElement | null {
    let el: HTMLElement | null = start;

    while (el) {
      const style = getComputedStyle(el);
      const canScrollY = /(auto|scroll|overlay)/.test(style.overflowY);
      if (canScrollY && el.scrollHeight > el.clientHeight) return el;
      el = el.parentElement;
    }

    return null;
  }
}
